import { ZodError, z } from "zod";

const RuntimeConfigSchema = z.object({
  pocketbaseUrl: z.url(),
  // Hides the username/password half of the login form so real users reach for
  // SSO, which is how every human account signs in. Off unless an environment
  // opts in (only prod does): dev, e2e, preview and staging keep the form so
  // seeded local users can still log in. The password grant itself stays
  // enabled — this is a nudge, not an access control — and the form is still
  // reachable behind the escape hatches in `lib/login-form-reveal.ts`.
  hidePasswordLogin: z.boolean().default(false),
  // Lets the portal pick up a session handed over in the URL fragment
  // (`#agent_token=<jwt>`) so agents and scripts can sign in to a preview env
  // without typing a password (FANL-1664). Only preview envs opt in
  // (`infra/scripts/preview-engage.sh`), and `lib/agent-login.ts` also refuses
  // any host that is not a `pr-<nr>.dev.froygruppen.no` preview or localhost.
  agentLogin: z.boolean().default(false),
});

type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

const config: RuntimeConfig = await loadRuntimeConfig();

export namespace Config {
  export async function pocketbaseUrl(): Promise<string> {
    return config.pocketbaseUrl;
  }

  // Sync, unlike `pocketbaseUrl`: the module-level `await` above has already
  // resolved by the time any component renders, and the login form needs the
  // answer on its first paint rather than one state update later.
  export function hidePasswordLogin(): boolean {
    return config.hidePasswordLogin;
  }

  export function agentLogin(): boolean {
    return config.agentLogin;
  }
}

/**
 * Load runtime configuration from /config.json
 * Returns the PocketBase URL or throws if not available
 */
async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const response = await fetch("/config.json", { cache: "no-store" });

    if (import.meta.env.DEV) {
      // Catches SPA fallback (200 with HTML) in dev
      // Ie. fetch("/config.json") -> returns 200 with HTML instead of 404.
      // But this is only with vite dev server; prod with nginx will return 404.
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.warn("Runtime config not supplied, using fallback for dev.");
        // Default/fallback config only for dev.
        // "Real" environment explicitly require and correct config to to be provided.
        return getFallbackConfig();
      }
    }

    if (!response.ok) {
      // Didn't try to supply a config.
      console.error("Failed to fetch /config.json, status:", response.status);
      throw new Error(`failed to fetch /config.json, status: ${response.status}`);
    }

    const configJson: unknown = await response.json();
    const parseConfigResult = RuntimeConfigSchema.safeParse(configJson);

    if (!parseConfigResult.success) {
      throw new Error(`invalid runtime config: ${parseConfigResult.error.message}`);
    }

    return parseConfigResult.data;
  } catch (error) {
    console.error("error loading runtime config:", error);
    showConfigError("invalid runtime config", error);
    throw new Error("Error loading runtime config");
  }
}

function getFallbackConfig(): RuntimeConfig {
  const fallback: RuntimeConfig = {
    pocketbaseUrl: import.meta.env.VITE_POCKETBASE_URL || "http://localhost:8090",
    hidePasswordLogin: false,
    agentLogin: false,
  };
  console.warn("Fallback config:", fallback);
  return fallback;
}

function showConfigError(title: string, error: unknown) {
  // Determine error content based on error type
  const errorContent =
    error instanceof ZodError
      ? error.issues.map((issue) => `• ${issue.path.join(".")}: ${issue.message}`).join("<br>")
      : error instanceof Error
        ? error.message
        : String(error);

  const errorDiv = document.createElement("div");
  errorDiv.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #fee;
    border: 2px solid #c00;
    border-radius: 8px;
    padding: 16px;
    max-width: 400px;
    z-index: 9999;
    font-family: monospace;
    font-size: 14px;
  `;

  errorDiv.innerHTML = `
    <strong style="color: #c00; display: block; margin-bottom: 8px;">${title}</strong>
    <div style="color: #600;">
      ${errorContent}
    </div>
  `;

  document.body.appendChild(errorDiv);
}
